import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { ScrollArea, Skeleton, Stack, Table, Text } from '@mantine/core';
import { useI18n } from '@/shared/i18n';
import { defaultDensity, densityTokens, type UiDensity } from '../tokens/density';
import { DataTableShell } from './DataTableShell';
import { EmptyState } from './EmptyState';
import { ErrorState } from './Surfaces';
import classes from './AppDataTable.module.css';

export interface AppDataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Pin column to the right edge while scrolling horizontally. */
  sticky?: 'right';
  /**
   * Optional compact control under the header label (e.g. NativeSelect column filter).
   * Prefer size="xs" / compact controls; no external field labels.
   */
  headerFilter?: ReactNode;
  width?: number | string;
  miw?: number | string;
  maw?: number | string;
}

function resolveSticky(column: AppDataTableColumn<unknown>): 'right' | undefined {
  if (column.sticky) {
    return column.sticky;
  }
  if (column.key === 'actions') {
    return 'right';
  }
  return undefined;
}

function stickyCellStyle(
  sticky?: 'right',
  miw?: number | string,
): CSSProperties | undefined {
  if (sticky !== 'right') {
    return undefined;
  }
  return {
    position: 'sticky',
    right: 0,
    zIndex: 3,
    minWidth: miw ?? undefined,
    background: 'var(--mantine-color-body)',
    boxShadow: '-8px 0 12px -10px rgba(15, 23, 42, 0.35)',
  };
}

function resolveTableMinWidth(minTableWidth?: number | string): CSSProperties {
  // Always fill the shell; optional minTableWidth only raises the floor for horizontal scroll.
  if (minTableWidth == null || minTableWidth === '') {
    return { minWidth: '100%' };
  }
  const floor = typeof minTableWidth === 'number' ? `${minTableWidth}px` : String(minTableWidth);
  return { minWidth: `max(100%, ${floor})` };
}

function HeaderCell({ column }: { column: AppDataTableColumn<unknown> }) {
  const sticky = resolveSticky(column);
  return (
    <Table.Th
      ta={column.align ?? 'left'}
      w={column.width}
      miw={column.miw}
      maw={column.maw}
      className={sticky === 'right' ? classes.stickyRight : undefined}
      style={stickyCellStyle(sticky, column.miw)}
    >
      <Stack
        gap={4}
        className={classes.headerCell}
        align={column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'stretch'}
      >
        <Text size="xs" tt="uppercase" fw={700} c="dimmed" lh={1.2}>
          {column.header}
        </Text>
        {column.headerFilter ? (
          <div className={classes.headerFilter}>{column.headerFilter}</div>
        ) : null}
      </Stack>
    </Table.Th>
  );
}

export function AppDataTable<T>({
  columns,
  rows,
  emptyTitle,
  emptyDescription,
  rowTestId,
  rowClassName,
  tableTestId,
  maxHeight = '70dvh',
  minTableWidth,
  density = defaultDensity,
  loading = false,
  errorMessage,
  onRowClick,
  getRowKey,
  fillHeight = false,
}: {
  columns: AppDataTableColumn<T>[];
  rows: T[];
  emptyTitle?: string;
  emptyDescription?: string;
  rowTestId?: (row: T) => string;
  rowClassName?: (row: T) => string | undefined;
  tableTestId?: string;
  maxHeight?: number | string;
  minTableWidth?: number | string;
  density?: UiDensity;
  loading?: boolean;
  errorMessage?: string;
  onRowClick?: (row: T) => void;
  getRowKey?: (row: T, index: number) => string | number;
  /** Fill parent height; scroll inside the table (workspace layout). */
  fillHeight?: boolean;
}) {
  const { t } = useI18n();
  const isEmpty = !loading && !errorMessage && rows.length === 0;
  const densityConfig = densityTokens[density];

  function rowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, row: T) {
    if (!onRowClick) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick(row);
    }
  }

  const table = (
    <Table
      highlightOnHover={!isEmpty && !loading}
      verticalSpacing={densityConfig.verticalSpacing}
      horizontalSpacing="sm"
      striped={isEmpty || loading ? undefined : 'odd'}
      data-testid={tableTestId}
      className={classes.table}
      fz="sm"
      style={resolveTableMinWidth(minTableWidth)}
    >
      <Table.Thead>
        <Table.Tr>
          {columns.map((column) => (
            <HeaderCell key={column.key} column={column as AppDataTableColumn<unknown>} />
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {errorMessage ? (
          <Table.Tr>
            <Table.Td colSpan={columns.length}>
              <ErrorState message={errorMessage} />
            </Table.Td>
          </Table.Tr>
        ) : loading ? (
          Array.from({ length: 5 }).map((_, rowIndex) => (
            <Table.Tr key={`skeleton-${rowIndex}`} data-testid="data-table-loading-row">
              {columns.map((column) => (
                <Table.Td key={column.key}>
                  <Skeleton height={12} radius="sm" />
                </Table.Td>
              ))}
            </Table.Tr>
          ))
        ) : isEmpty ? (
          <Table.Tr>
            <Table.Td colSpan={columns.length}>
              <EmptyState
                title={emptyTitle ?? t('common.empty.title')}
                description={emptyDescription ?? t('common.empty.description')}
              />
            </Table.Td>
          </Table.Tr>
        ) : (
          rows.map((row, index) => (
            <Table.Tr
              key={getRowKey?.(row, index) ?? index}
              data-testid={rowTestId?.(row)}
              className={rowClassName?.(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? (event) => rowKeyDown(event, row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((column) => {
                const sticky = resolveSticky(column as AppDataTableColumn<unknown>);
                return (
                  <Table.Td
                    key={column.key}
                    ta={column.align ?? 'left'}
                    w={column.width}
                    miw={column.miw}
                    maw={column.maw}
                    className={sticky === 'right' ? classes.stickyRight : undefined}
                    style={stickyCellStyle(sticky, column.miw)}
                  >
                    {column.render(row)}
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))
        )}
      </Table.Tbody>
    </Table>
  );

  return (
    <DataTableShell fillHeight={fillHeight}>
      {fillHeight ? (
        <ScrollArea
          h="100%"
          type="auto"
          offsetScrollbars
          scrollbarSize={10}
          className={`${classes.scrollArea} ${classes.fillScroll}`}
        >
          {table}
        </ScrollArea>
      ) : (
        <ScrollArea.Autosize
          mah={maxHeight}
          type="auto"
          offsetScrollbars
          scrollbarSize={10}
          className={classes.scrollArea}
        >
          {table}
        </ScrollArea.Autosize>
      )}
    </DataTableShell>
  );
}
